import React from 'react';
import { useTranslation } from 'react-i18next';
import { FunnelV3Step } from '../../store/funnelV3Store';
import {
    getCompletedQuestionCount,
    getPreviousStep,
    getQuestionSteps,
    getSectionQuestionSteps,
    getStepMeta,
    shouldShowGlobalBack,
    shouldShowGlobalProgress,
} from './funnelV3StepConfig';

interface FunnelV3ProgressBarProps {
    currentStep: FunnelV3Step;
    setStep: (step: FunnelV3Step) => void;
}

interface Section {
    label: string;
    steps: FunnelV3Step[];
}

function getSectionDefinitions(t: (key: string) => string): Section[] {
    return [
        { label: t('progressBar.aboutYou'), steps: getSectionQuestionSteps('about_you') },
        { label: t('progressBar.yourBody'), steps: getSectionQuestionSteps('your_body') },
        { label: t('progressBar.yourPlan'), steps: getSectionQuestionSteps('your_plan') },
    ];
}

function getSectionProgress(section: Section, completedQuestions: number, allQuestionSteps: FunnelV3Step[]): number {
    if (section.steps.length === 0) return 0;
    const firstQuestionIdx = allQuestionSteps.indexOf(section.steps[0]);
    if (firstQuestionIdx < 0) return 0;
    const completedInSection = Math.min(
        Math.max(completedQuestions - firstQuestionIdx, 0),
        section.steps.length
    );
    return (completedInSection / section.steps.length) * 100;
}

function getActiveSection(sectionList: Section[], currentStep: FunnelV3Step): number {
    const currentSection = getStepMeta(currentStep).section;
    if (!currentSection) return 0;
    for (let i = 0; i < sectionList.length; i++) {
        const firstStep = sectionList[i].steps[0];
        if (firstStep && getStepMeta(firstStep).section === currentSection) return i;
    }
    return 0;
}

const FunnelV3ProgressBar: React.FC<FunnelV3ProgressBarProps> = ({ currentStep, setStep }) => {
    // @ts-ignore — react-i18next deep type issue with TS 4.x
    const { t } = useTranslation() as any;
    const isVisible = shouldShowGlobalProgress(currentStep);
    const isBackVisible = shouldShowGlobalBack(currentStep);
    const allQuestionSteps = getQuestionSteps();
    const completedQuestions = getCompletedQuestionCount(currentStep);
    const sections = getSectionDefinitions(t);
    const activeSection = getActiveSection(sections, currentStep);
    const ariaPercent = allQuestionSteps.length > 0
        ? Math.round((completedQuestions / allQuestionSteps.length) * 100)
        : 0;

    const handleBack = () => {
        const previousStep = getPreviousStep(currentStep);
        if (previousStep) setStep(previousStep);
    };

    // When hidden, render nothing so the step doesn't reserve the sticky bar's
    // vertical padding (matters for the first/motivation screen).
    if (!isVisible) return null;

    return (
        <div className="sticky top-0 z-30 bg-white" style={{ padding: '14px 20px 12px' }}>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleBack}
                    className={`w-9 h-9 rounded-full bg-white border border-navy flex items-center justify-center shrink-0 ${isBackVisible ? '' : 'opacity-0 pointer-events-none'}`}
                    aria-label="Go back"
                    tabIndex={isBackVisible ? 0 : -1}
                >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path
                            d="M11 4L6 9L11 14"
                            stroke="#0F1F3D"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                <div
                    className="flex-1 flex gap-2"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={allQuestionSteps.length}
                    aria-valuenow={completedQuestions}
                    aria-valuetext={`${ariaPercent}%`}
                    aria-label={t('progressBar.yourPlan')}
                >
                    {sections.map((section, i) => {
                        const progress = getSectionProgress(section, completedQuestions, allQuestionSteps);
                        const isActive = i === activeSection;
                        const isPast = i < activeSection;

                        return (
                            <div key={section.label} className="flex-1 min-w-0">
                                <span
                                    className={`block text-[10px] uppercase tracking-[0.5px] mb-1.5 text-center ${
                                        isActive
                                            ? 'font-bold text-[#0F1F3D]'
                                            : isPast
                                                ? 'font-semibold text-[#0F1F3D]'
                                                : 'font-medium text-[#7A90AB]'
                                    }`}
                                >
                                    {section.label}
                                </span>
                                <div className="w-full rounded-[99px] overflow-hidden" style={{ height: 5, backgroundColor: '#DDE9F5' }}>
                                    <div
                                        className="h-full rounded-[99px]"
                                        style={{
                                            width: `${progress}%`,
                                            background: '#369fff',
                                            transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default FunnelV3ProgressBar;
